import { Trans } from '@lingui/react';
import { Button } from '@startuptools/ui/Button';
import { Alert } from '@startuptools/ui/Alert';
import { Stack } from '@startuptools/ui/Stack';
import { VotingListTable } from './VotingListTable';
import {
  useCreateMeetingParticipantMutation,
  useDeleteMeetingShareholdersMutation,
  useMpImportShareStateMutation,
  MeetingParticipantsDocument,
} from '../../../graphql/react-operations';
import { ParticipantsSummonsDocument } from '../../meetings/components/edit-steps/ParticipantsSummons.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { AlertDialog } from '../../../components/simple-dialogs/AlertDialog';
import { type ComponentProps, useState } from 'react';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { InputState } from '@startuptools/business-ui/identity/types';
import { JuridicalKind } from '../../../models/identity.model';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { GqlMeeting, GqlMpKind } from '../../../graphql/base-types.graphql';
import { MeetingParticipantsTableInnerDocument } from '../../meetings/components/MeetingParticipantsTable.graphql';
import { PowerOfAttorneysDocument } from './edit-steps/PowerOfAttorneys.graphql';
import { ShareholdersMeetingDocument } from '../ShareholdersMeeting.graphql';
import { toast } from '@startuptools/ui/toast/use-toast';
import { ApolloError } from '@apollo/client';

export const VotingList = ({ meeting }: { meeting: Pick<GqlMeeting, 'id' | 'startTime'> }) => {
  const companyId = useCompanyId();
  // eslint-disable-next-line sut/loading-mutation
  const [doDeleteAll] = useDeleteMeetingShareholdersMutation({
    variables: { companyId, meetingId: meeting.id },
    refetchQueries: [
      ShareholdersMeetingDocument,
      MeetingParticipantsDocument,
      ParticipantsSummonsDocument,
      MeetingParticipantsTableInnerDocument,
      PowerOfAttorneysDocument,
    ],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doSync] = useMpImportShareStateMutation({
    refetchQueries: [
      ShareholdersMeetingDocument,
      MeetingParticipantsDocument,
      ParticipantsSummonsDocument,
      MeetingParticipantsTableInnerDocument,
      PowerOfAttorneysDocument,
    ],
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doCreate] = useCreateMeetingParticipantMutation({
    refetchQueries: [
      ShareholdersMeetingDocument,
      MeetingParticipantsDocument,
      ParticipantsSummonsDocument,
      MeetingParticipantsTableInnerDocument,
      PowerOfAttorneysDocument,
    ],
  });

  const [deleteAllDialogProps, setDeleteAllDialogProps] = useState<Pick<
    ComponentProps<typeof AlertDialog>,
    'onAccept'
  > | null>(null);
  const [openAddMeetingParticipant, setOpenAddMeetingParticipant] = useState<boolean>(false);

  const handleSync = async () => {
    await doSync({ variables: { companyId, input: { meetingId: meeting.id, date: meeting.startTime } } });
  };

  const handleDeleteAll = () => {
    setDeleteAllDialogProps({
      onAccept: async () => {
        await doDeleteAll();
        setDeleteAllDialogProps(null);
      },
    });
  };

  return (
    <>
      <Stack direction="column" gap={4}>
        <Stack direction="row" gap={2}>
          <Button onClick={handleSync} variant="outlined">
            <Trans id="Sync from share register" />
          </Button>
          <Button onClick={() => setOpenAddMeetingParticipant(true)} variant="outlined">
            <Trans id="Add meeting participant" />
          </Button>
          <Button color="warning" onClick={handleDeleteAll} variant="outlined">
            <Trans id="Delete all" />
          </Button>
        </Stack>
        <VotingListTable meetingId={meeting.id} />
      </Stack>
      {deleteAllDialogProps && (
        <AlertDialog
          title={<Trans id="Deleting all shareholders from voting list" />}
          onCancel={() => setDeleteAllDialogProps(null)}
          {...deleteAllDialogProps}
        >
          <Alert color="warning">
            <Trans id="This will delete all entries from the voting list, do you want to continue?" />
          </Alert>
        </AlertDialog>
      )}
      {openAddMeetingParticipant && (
        <AddIdentity
          detailFieldsSettings={{}}
          headline={<Trans id="Add meeting participant" />}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
            [InputState.CompanyBoard]: false,
          }}
          juridicalKinds={[JuridicalKind.Person, JuridicalKind.Company]}
          relations={{
            shareholder: true,
            companyDirector: true,
            meetingParticipant: true,
          }}
          onClose={() => setOpenAddMeetingParticipant(false)}
          handleCreate={async identity => {
            try {
              await doCreate({
                variables: {
                  companyId,
                  input: {
                    adjuster: false,
                    chairman: false,
                    secretary: false,
                    present: true,
                    meetingId: meeting.id,
                    isShareholder: true,
                    kind: GqlMpKind.Shareholder,
                    identityInput: identityToInput(identity),
                  },
                },
              });
            } catch (error) {
              if (error instanceof ApolloError && error.message === 'DUPLICATE_SHAREHOLDER') {
                toast({
                  severity: 'warning',
                  description: <Trans id="The shareholder already exists in the voting list" />,
                });
              }
              throw error; // keep the dialog open
            }
          }}
        />
      )}
    </>
  );
};
